/**
 * Marketplace moderation constants and utilities
 * Based on the old app's marketplace keyword detection
 */

import { Group } from '@cobu/api-client';

// Marketplace keywords to detect marketplace content
export const MARKETPLACE_KEYWORDS = [
  'sell', 'buy', 'sale', 'selling', 'buying', 'trade', 'trading',
  'swap', 'swapping', 'exchange', 'exchanging', 'offer', 'offering',
  'wanted', 'need', 'looking for', 'seeking', 'price',
  'cost', 'fee', 'payment', 'money', 'cash', 'free', 'giveaway',
  'donate', 'donation', 'marketplace', 'market', 'store', 'shop',
  'shopping', 'purchase', 'purchasing', 'acquire', 'acquiring'
];

// Content moderation status codes
export const CREATE_COMMENT_STATUS = {
  CREATED: 200,
  SHOW_MODERATION_ALERT: 202,
} as const;

// Marketplace group name patterns (from old app)
export const MARKETPLACE_NAME_PATTERN = /marketplace|sell|buy|trade/i;

/**
 * Check if content contains marketplace keywords
 * @param content - The comment content to check
 * @returns boolean - True if marketplace content is detected
 */
export const isMarketplaceContent = (content: string): boolean => {
  if (!content) {
    return false;
  }

  // Escape special regex characters in keywords
  const escapedKeywords = MARKETPLACE_KEYWORDS.map(keyword =>
    keyword.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
  );

  // Create pattern to match word boundaries and dollar sign
  const pattern = new RegExp(`\\b(?:${escapedKeywords.join('|')})\\b|\\$`, 'i');
  return pattern.test(content);
};

/**
 * Find marketplace group from groups list
 * Based on the old app's marketplace group identification logic
 * @param groups - Array of groups
 * @returns Group | undefined - Marketplace group if found
 */
export const findMarketplaceGroup = (groups: Group[]): Group | undefined => {
  if (!groups || groups.length === 0) {
    return undefined;
  }

  // First, check if any group is explicitly marked as marketplace
  const explicitMarketplaceGroup = groups.find((group: Group) => group.isMarketplace === true);
  if (explicitMarketplaceGroup) {
    return explicitMarketplaceGroup;
  }

  // Second, check group names using the pattern from the old app
  const marketplaceGroup = groups.find((group: Group) =>
    MARKETPLACE_NAME_PATTERN.test(group.name)
  );
  
  return marketplaceGroup;
};

/**
 * Legacy function for backwards compatibility
 * @param groups - Array of groups
 * @returns string | undefined - Marketplace group UUID if found
 */
export const findMarketplaceGroupId = (groups: Group[]): string | undefined => {
  const marketplaceGroup = findMarketplaceGroup(groups);
  return marketplaceGroup?.uuid;
}; 